import { useContext, useRef } from 'react';

import { cx } from 'cva';
import {
  DisclosureStateContext,
  Heading,
  type HeadingProps,
  Disclosure as RACDisclosure,
  DisclosurePanel as RACDisclosurePanel,
  type DisclosurePanelProps as RACDisclosurePanelProps,
  type DisclosureProps as RACDisclosureProps,
} from 'react-aria-components/Disclosure';
import {
  DisclosureGroup as RACDisclosureGroup,
  type DisclosureGroupProps as RACDisclosureGroupProps,
} from 'react-aria-components/DisclosureGroup';

import { Button } from '../button/button';
import { Icon } from '../icon/icon';

import styles from './disclosure.module.css';

/**
 * Disclosure components.
 *
 * This file contains a set of components to create a disclosure,
 * an expanding/collapsing panel that can be toggled open or closed.
 *
 * The main components are:
 * * `Disclosure` - The main container of the disclosure.
 * * `DisclosureHeader` - The header of the disclosure that contains the trigger button.
 * * `DisclosurePanel` - The content panel that is shown or hidden based on the disclosure state.
 * * `DisclosureGroup` - Groups related disclosures (an accordion). Each child `Disclosure` needs an `id`.
 *
 * The `DisclosurePanel` can be configured to lazy mount its children,
 * meaning that the children will not be rendered until the disclosure is opened for the first time.
 *
 *
 * Example usage of the Disclosure components:
 *  *
 * ```tsx
 * <Disclosure>
 *  <DisclosureHeader>Trigger me!</DisclosureHeader>
 *  <DisclosurePanel>
 *    <Text variant="label">Available filters are: Date, Type, and Status.</Text>
 *  </DisclosurePanel>
 * </Disclosure>
 * ```
 */

export type DisclosureProps = RACDisclosureProps;

export const Disclosure = ({
  children,
  className,
  ...props
}: DisclosureProps) => {
  return (
    <RACDisclosure {...props} className={cx(styles.disclosure, className)}>
      {children}
    </RACDisclosure>
  );
};

export type DisclosurePanelProps = RACDisclosurePanelProps & {
  lazyMount?: boolean;
  position?: 'bottom' | 'right';
};

export const DisclosurePanel = ({
  children,
  className,
  lazyMount,
  position = 'bottom',
  ...props
}: DisclosurePanelProps) => {
  const state = useContext(DisclosureStateContext);
  const hasMountedRef = useRef(false);

  if (state?.isExpanded) {
    hasMountedRef.current = true;
  }

  const shouldRenderChildren = !lazyMount || hasMountedRef.current;

  return (
    <RACDisclosurePanel
      {...props}
      data-position={position}
      className={cx(styles.panel, className)}
    >
      {shouldRenderChildren ? children : null}
    </RACDisclosurePanel>
  );
};

export const DisclosureHeader = ({
  children,
  className,
  ...props
}: HeadingProps) => {
  return (
    <Heading {...props} className={cx(styles.header, className)}>
      <Button
        slot="trigger"
        variant="tertiary"
        icon={<Icon name="chevron_small_isolated_e" size="xs" />}
        className={styles.disclosure_button}
      >
        {children}
      </Button>
    </Heading>
  );
};

export type DisclosureGroupProps = RACDisclosureGroupProps;

export const DisclosureGroup = ({
  children,
  className,
  ...props
}: DisclosureGroupProps) => {
  return (
    <RACDisclosureGroup
      {...props}
      className={cx(styles.disclosure_group, className)}
    >
      {children}
    </RACDisclosureGroup>
  );
};
